import { QueryClient, useQuery } from 'react-query';
import { Auth } from 'aws-amplify';
import { useMemo } from 'react';
import { isAfter } from 'date-fns';
import { appsyncGraphql, publicAppsyncGraphql } from './appsyncHelper';
import {
  MutationMakeAProductSubscriptionArgs,
  QueryGetMyProductSubscriptionsArgs,
  QueryGetProductByIdArgs,
  ProductSubscriptionStatus,
  QueryGetProductSubscriptionArgs,
  MutationCancelAProductSubscriptionArgs,
  MutationRetrieveSubscriptionClientSecretArgs,
  QueryGetProductsByPodIdArgs,
  Maybe,
  ProductSubscription,
  ProductPaymentType,
} from '../types/appsync-types';
import { useMyProfileQuery } from './appsyncApis';
import { invalidateReservationData } from './reservationApis';
import { useNowMinutes } from '../hooks/useNow';
import { useGetPoisByParentId } from './mapApis';
import { isAfterOrEqual } from '../utils/helpers';

useGetProducts.id = 'useGetProducts';

export function useGetProducts(enabled: boolean = true) {
  return useQuery(
    [useGetProducts.id],
    async () => {
      const query = /* GraphQL */ `
        query GetProducts {
          getProducts {
            id
            name
            price
            paymentType
            oneTimeLength
            renewInterval
            owner {
              name
            }
            subscription {
              id
              currency
              price
              status
              renewalDate
              endsAt
            }
          }
        }
      `;

      let user;
      try {
        user = await Auth.currentAuthenticatedUser();
      } catch (error) {
        /* */
      }
      if (user) {
        return appsyncGraphql<'getProducts'>(query);
      }
      return publicAppsyncGraphql<'getProducts'>(query);
    },
    { enabled },
  );
}

useGetProductsByPodId.id = 'useGetProductsByPodId';

/** Fetches products that include `podId`, or all products if no `podId` is provided */
export function useGetProductsByPodId(podId: string | undefined | null, enabled: boolean = true) {
  return useQuery(
    [useGetProductsByPodId.id, podId],
    async () => {
      if (podId) {
        const variables: QueryGetProductsByPodIdArgs = {
          id: podId,
        };
        const query = /* GraphQL */ `
          query GetProductsByPodId($id: ID!) {
            getProductsByPodId(id: $id) {
              id
              name
              price
              paymentType
              oneTimeLength
              renewInterval
              requiredAuth
              owner {
                name
              }
              subscription {
                id
                currency
                price
                status
                renewalDate
                startDate
                endsAt
              }
            }
          }
        `;

        let user;
        try {
          user = await Auth.currentAuthenticatedUser();
        } catch (error) {
          /* */
        }
        if (user) {
          return appsyncGraphql<'getProductsByPodId' | 'getProducts'>(query, variables);
        }
        return publicAppsyncGraphql<'getProductsByPodId' | 'getProducts'>(query, variables);
      }

      const query = /* GraphQL */ `
        query GetProducts {
          getProducts {
            id
            name
            price
            paymentType
            oneTimeLength
            renewInterval
            owner {
              name
            }
            subscription {
              id
              currency
              price
              status
              renewalDate
              startDate
              endsAt
            }
          }
        }
      `;

      let user;
      try {
        user = await Auth.currentAuthenticatedUser();
      } catch (error) {
        /* */
      }
      if (user) {
        return appsyncGraphql<'getProductsByPodId' | 'getProducts'>(query);
      }
      return publicAppsyncGraphql<'getProductsByPodId' | 'getProducts'>(query);
    },
    { enabled },
  );
}

useGetProductById.id = 'useGetProductById';

export function useGetProductById(productId: string | undefined, keepUpToDate = false) {
  const now = useNowMinutes();
  const queryKeys: (string | undefined | Date)[] = [useGetProductById.id, productId];
  if (keepUpToDate) {
    queryKeys.push(now);
  }
  return useQuery(
    queryKeys,
    async () => {
      const variables: QueryGetProductByIdArgs = {
        id: productId!,
      };
      const query = /* GraphQL */ `
        query GetProductById($id: ID!) {
          getProductById(id: $id) {
            id
            name
            serviceType
            price
            chargeRates {
              min
              max
              interval
              price
            }
            paymentType
            oneTimeLength
            renewInterval
            requiredAuth
            terms {
              lang
              url
            }
            linkedPods {
              localRef
              parentRef
              title
              spaceType
              building {
                title
                timezone
              }
              resources {
                title
                resourceId
              }
            }
            owner {
              name
            }
            subscription {
              id
              stripeSubscriptionId
              price
              currency
              startDate
              renewalDate
              endsAt
              cancelDate
              status
              receiptUrl
              card {
                id
                last4
                brand
                is_default
              }
            }
          }
        }
      `;

      let user;
      try {
        user = await Auth.currentAuthenticatedUser();
      } catch (error) {
        /* */
      }
      if (user) {
        return appsyncGraphql<'getProductById'>(query, variables);
      }
      return publicAppsyncGraphql<'getProductById'>(query, variables);
    },
    { enabled: !!productId, keepPreviousData: keepUpToDate },
  );
}

getMySubscriptions.id = 'getMySubscriptions';

function getMySubscriptions() {
  const query = /* GraphQL */ `
    query GetMyProductSubscriptions {
      getMyProductSubscriptions {
        id
        productId
        status
        userId
        receiptUrl
        card {
          id
          last4
          brand
        }
        product {
          id
        }
      }
    }
  `;
  return appsyncGraphql<'getMyProductSubscriptions'>(query);
}

useGetMyUpcomingReservations.id = 'useGetMyUpcomingReservations';

export function useGetMyUpcomingReservations() {
  return useQuery([useGetProductById.id], async () => {
    const query = /* GraphQL */ `
      query GetMyUpcomingReservations {
        getMyUpcomingReservations {
          id
          productId
          status
          startDate
          product {
            linkedPods {
              localRef
            }
          }
        }
      }
    `;
    return appsyncGraphql<'getMyUpcomingReservations'>(query);
  });
}

useGetMyUnpaidReservations.id = 'useGetMyUnpaidReservations';

export function useGetMyUnpaidReservations() {
  const now = useNowMinutes();
  return useQuery(
    [useGetMyUnpaidReservations.id, now],
    async () => {
      const query = /* GraphQL */ `
        query GetMyUnpaidReservations {
          getMyUnpaidReservations {
            id
            productId
            status
            startDate
            product {
              name
              serviceType
              linkedPods {
                localRef
              }
            }
          }
        }
      `;
      return appsyncGraphql<'getMyUnpaidReservations'>(query);
    },
    { keepPreviousData: true },
  );
}

useGetMyUnpaidReservationsForProductsList.id = 'useGetMyUnpaidReservationsForProductsList';

export function useGetMyUnpaidReservationsForProductsList() {
  return useQuery([useGetMyUnpaidReservations.id], async () => {
    const query = /* GraphQL */ `
      query GetMyUnpaidReservations {
        getMyUnpaidReservations {
          id
          productId
          price
          currency
          status
          startDate
          endsAt
          product {
            id
            name
            owner {
              name
            }
            linkedPods {
              localRef
            }
          }
        }
      }
    `;
    return appsyncGraphql<'getMyUnpaidReservations'>(query);
  });
}

useGetMyOngoingProductSubscriptions.id = 'useGetMyOngoingProductSubscriptions';

export function useGetMyOngoingProductSubscriptions() {
  return useQuery([useGetMyOngoingProductSubscriptions.id], async () => {
    const query = /* GraphQL */ `
      query GetMyOngoingProductSubscriptionss {
        getMyOngoingProductSubscriptions {
          id
          productId
          status
          startDate
          product {
            linkedPods {
              localRef
            }
          }
        }
      }
    `;
    return appsyncGraphql<'getMyOngoingProductSubscriptions'>(query);
  });
}

useGetMyProductSubscriptions.id = 'useGetMyProductSubscriptions';

export function useGetMyProductSubscriptions() {
  return useQuery(
    [useGetMyProductSubscriptions.id],
    async () => {
      const query = /* GraphQL */ `
        query GetMyProductSubscriptions {
          getMyProductSubscriptions {
            id
            productId
            currency
            price
            status
            startDate
            product {
              paymentType
              linkedPods {
                localRef
                title
                resources {
                  title
                  resourceId
                }
              }
            }
          }
        }
      `;
      return appsyncGraphql<'getMyProductSubscriptions'>(query);
    },
    {},
  );
}

useGetMyUpToDateProductSubscriptions.id = 'useGetMyUpToDateProductSubscriptions';

export function useGetMyUpToDateProductSubscriptions() {
  const now = useNowMinutes();
  return useQuery(
    [useGetMyUpToDateProductSubscriptions.id, now],
    async () => {
      const query = /* GraphQL */ `
        query GetMyProductSubscriptions {
          getMyProductSubscriptions {
            id
            productId
            status
            startDate
            product {
              name
              serviceType
              paymentType
              linkedPods {
                localRef
                resources {
                  title
                  resourceId
                }
              }
            }
          }
        }
      `;
      return appsyncGraphql<'getMyProductSubscriptions'>(query);
    },
    { keepPreviousData: true }, // TODO: Make sure this doesn't cause any issues
  );
}

export function useMyProductReservations() {
  const now = useNowMinutes();
  const ongoingAndUpcomingReservationsResult = useGetMyUpToDateProductSubscriptions();
  const unpaidReservationsResult = useGetMyUnpaidReservations();

  const reservations = useMemo(() => {
    const { ongoingReservations, upcomingReservations, rest } = (
      ongoingAndUpcomingReservationsResult.data?.data?.getMyProductSubscriptions ?? []
    ).reduce(
      (result, subscription) => {
        if (subscription?.product?.paymentType !== ProductPaymentType.RESERVATION) return result;
        if (subscription?.status === ProductSubscriptionStatus.RESERVED && subscription.startDate) {
          isAfterOrEqual(now, new Date(subscription.startDate))
            ? result.ongoingReservations.push(subscription)
            : result.upcomingReservations.push(subscription);
        } else {
          result.rest.push(subscription);
        }
        return result;
      },
      {
        ongoingReservations: [] as Maybe<ProductSubscription>[],
        upcomingReservations: [] as Maybe<ProductSubscription>[],
        rest: [] as Maybe<ProductSubscription>[],
      },
    );

    return {
      ongoingReservations,
      upcomingReservations,
      unpaidReservations: unpaidReservationsResult.data?.data?.getMyUnpaidReservations ?? [],
      rest,
    };
  }, [
    ongoingAndUpcomingReservationsResult.data?.data?.getMyProductSubscriptions,
    unpaidReservationsResult.data?.data?.getMyUnpaidReservations,
    now,
  ]);

  return {
    isLoading: ongoingAndUpcomingReservationsResult.isLoading || unpaidReservationsResult.isLoading,
    ...reservations,
  };
}

useGetProductSubscription.id = 'useGetProductSubscription';

export function useGetProductSubscription(subscriptionId: string | undefined) {
  return useQuery(
    [useGetProductSubscription.id, subscriptionId],
    async () => {
      const variables: QueryGetProductSubscriptionArgs = {
        id: subscriptionId!,
      };
      const query = /* GraphQL */ `
        query GetProductSubscription($id: ID!) {
          getProductSubscription(id: $id) {
            id
            price
            currency
            status
            startDate
            renewalDate
            endsAt
            cancelDate
            card {
              id
              last4
              brand
            }
            product {
              id
              name
              serviceType
              price
              linkedPods {
                localRef
                title
                building {
                  title
                }
              }
              owner {
                name
              }
            }
          }
        }
      `;

      return appsyncGraphql<'getProductSubscription'>(query, variables);
    },
    { enabled: !!subscriptionId },
  );
}

function combineStatus(a: ProductSubscriptionStatus, b: ProductSubscriptionStatus): ProductSubscriptionStatus {
  if ([a, b].includes(ProductSubscriptionStatus.MISSING_PAYMENT)) {
    return ProductSubscriptionStatus.MISSING_PAYMENT;
  }
  if ([a, b].includes(ProductSubscriptionStatus.ACTIVE)) {
    return ProductSubscriptionStatus.ACTIVE;
  }
  if ([a, b].includes(ProductSubscriptionStatus.ENDED)) {
    return ProductSubscriptionStatus.ENDED;
  }
  if ([a, b].includes(ProductSubscriptionStatus.CANCELLED)) {
    return ProductSubscriptionStatus.CANCELLED;
  }
  if ([a, b].includes(ProductSubscriptionStatus.FAILED)) {
    return ProductSubscriptionStatus.FAILED;
  }
  return a ?? b;
}

export function createSubscription(input: MutationMakeAProductSubscriptionArgs) {
  const mutation = /* GraphQL */ `
    mutation MakeAProductSubscription($input: ProductSubscriptionInput) {
      makeAProductSubscription(input: $input) {
        id
        stripeSubscriptionId
        status
        currency
        userId
        productId
        product {
          id
          name
          price
        }
      }
    }
  `;
  return appsyncGraphql<'makeAProductSubscription'>(mutation, input);
}

export function retrieveSubscriptionClientSecret(input: MutationRetrieveSubscriptionClientSecretArgs) {
  const mutation = /* GraphQL */ `
    mutation RetrieveSubscriptionClientSecret($subscriptionId: String) {
      retrieveSubscriptionClientSecret(subscription_id: $subscriptionId)
    }
  `;
  return appsyncGraphql<'retrieveSubscriptionClientSecret'>(mutation, input);
}

export function cancelSubscription(input: MutationCancelAProductSubscriptionArgs) {
  const mutation = /* GraphQL */ `
    mutation CancelASubscription($id: ID!) {
      cancelAProductSubscription(id: $id) {
        id
      }
    }
  `;
  return appsyncGraphql<'makeAProductSubscription'>(mutation, input);
}

export function invalidateSubscriptionData(queryClient: QueryClient) {
  queryClient.invalidateQueries(getMySubscriptions.id);
  queryClient.invalidateQueries(useGetProductSubscription.id);
  queryClient.invalidateQueries(useGetMyProductSubscriptions.id);
  queryClient.invalidateQueries(useGetProductById.id);
  queryClient.invalidateQueries(useGetProducts.id);
  queryClient.invalidateQueries(useGetProductsByPodId.id);
  queryClient.invalidateQueries(useGetMyUpcomingReservations.id);
  queryClient.invalidateQueries(useGetMyUnpaidReservations.id);
  queryClient.invalidateQueries(useGetMyUnpaidReservationsForProductsList.id);
  queryClient.invalidateQueries(useGetMyOngoingProductSubscriptions.id);
  queryClient.invalidateQueries(useGetMyUpToDateProductSubscriptions.id);
  // Keep locker statuses up to date
  queryClient.invalidateQueries(useGetPoisByParentId.id);

  invalidateReservationData(queryClient);
}
